import { useCallback, useEffect, useId, type FC } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { SvgIcon } from './SellerRegistrationForm';

// ─── LiveRegion ───────────────────────────────────────────────────────────────
// Visually-hidden live region (WCAG 4.1.3 Status Messages).
// Invisible to sighted users; announced to screen readers on message change.
interface LiveRegionProps {
  message: string;
  politeness?: 'polite' | 'assertive';
}

const LiveRegion: FC<LiveRegionProps> = ({ message, politeness = 'polite' }) => (
  <span
    role={politeness === 'assertive' ? 'alert' : 'status'}
    aria-live={politeness}
    aria-atomic="true"
    style={{
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      borderWidth: 0,
    }}
  >
    {message}
  </span>
);

// ─── ConnectionApproach ───────────────────────────────────────────────────────
const ConnectionApproach: FC = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Stable ID for aria-labelledby on the <section> landmark (WCAG 1.3.1, 2.4.6)
  const headingId = useId();

  // Respects prefers-reduced-motion before scrolling (WCAG 2.3.3)
  const handleNavigate = useCallback(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}${t('registrationStepsData.connectionOptionsUrl')}`);
  }, [navigate, i18n.language, t]);

  // Image paths sourced from translations to support per-locale asset overrides
  const imageSrc = isSm
    ? t('registrationStepsData.apologyMessage.imageSm')
    : t('registrationStepsData.apologyMessage.imageDefault');

  const handleSubscribe = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}/notifications-preferences`);
  }

  return (
    <Container
      component="section"
      aria-labelledby={headingId}
      disableGutters
      sx={{
        padding: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        py: { xs: 4, sm: 6, md: 8 },
        mt: { xs: 4, sm: 0, md: 0 },
      }}
    >
      {/* WCAG 4.1.3 – polite live region announces the page context to screen readers */}
      <LiveRegion message={t('registrationStepsData.apologyMessage.pageAnnouncement')} />

      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row', md: 'row' },
          alignItems: 'center',
          width: '100%',
          maxWidth: '1200px',
          mx: { xs: '16px', sm: '32px', md: '32px' },
          gap: { xs: 4, sm: '0px', md: '0px' },
        }}
      >
        {/* Text section */}
        <Grid
          item
          xs={12}
          sm={8}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            pr: { xs: 0, sm: 3, md: 3 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Heading referenced by aria-labelledby on the section landmark */}
            <Typography
              id={headingId}
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '24px', sm: '28px', md: '32px' },
              }}
            >
              {t('registrationStepsData.apologyMessage.title')}
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontSize: { xs: '18px' }, fontWeight: 400 }}
            >
              <Trans
                i18nKey="registrationStepsData.apologyMessage.body1"
                components={
                  {
                    a: <a style={{ cursor: 'pointer' }} onClick={handleSubscribe}></a>
                  }
                }
              />
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '18px', fontWeight: 400 }}>
              {t('registrationStepsData.apologyMessage.body2')}
            </Typography>
          </Box>

          {/*
            WCAG 2.1.1 – keyboard operable: <button> natively handles Enter/Space.
            WCAG 2.4.6 – descriptive label provided by visible text.
            WCAG 2.5.8 – MUI size="md" meets the 24×24 px minimum target size.
          */}
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleNavigate}
            sx={{ display:'flex', gap:'6px', justifyContent:'center', alignItems:'center', minWidth: 'auto !important' }}
          >
            {t('registrationStepsData.apologyMessage.cta')}
            <SvgIcon
            src='/images/registration/arrowRight.svg'
            size={12}
            color='white'
            ></SvgIcon>
          </Button>
        </Grid>

        {/* Decorative illustration – hidden from assistive technology (WCAG 1.1.1) */}
        <Grid
          item
          xs={12}
          sm={4}
          md={6}
          sx={{
            pl: { xs: 0, sm: 2, md: 2 },
            borderRadius: 3,
            width: '100%',
            background: 'linear-gradient(180deg, #CCEAF8 0%, #F1F7FB 100%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={imageSrc}
            alt=""
            aria-hidden="true"
            sx={{
              width: '100%',
              objectFit: 'contain',
              display: 'block',
              minHeight: { xs: '100%', sm: '350px', md: '480px' },
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ConnectionApproach;